import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { filter, finalize } from 'rxjs';
import { CurrentContextService } from '../../../core/context/current-context.service';
import { LiveUpdateEventTypes, growthPaidMediaTopic } from '../../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../../core/live-updates/live-update.service';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../../shared/design-system/page.component';
import { AinStateComponent } from '../../../shared/design-system/state.component';
import { StructuredJsonDisplayComponent } from '../../../shared/design-system/structured-json-display.component';
import {
  GrowthPaidMediaAccountDto,
  growthPaidMediaCapabilitiesFields
} from './growth-paid-media.models';
import { GrowthPaidMediaService } from './growth-paid-media.service';

@Component({
  standalone: true,
  selector: 'ain-growth-paid-media-account-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    AinFormFieldComponent,
    StructuredJsonDisplayComponent,
    RouterLink,
    FormsModule,
    ButtonModule,
    InputTextModule,
    DialogModule
  ],
  template: `
    <ain-page
      [title]="account() ? providerLabel(account()!.providerKey) : t('growth.paidMedia.accountDetail.title')"
      [description]="t('growth.paidMedia.accountDetail.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.paidMedia.accountDetail.help.title'"
        [descriptionKey]="'growth.paidMedia.accountDetail.help.description'"
        [bulletKeys]="['growth.paidMedia.accountDetail.help.bullets.capabilities', 'growth.paidMedia.accountDetail.help.bullets.residency']" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (!account()) {
        <ain-state type="empty" [title]="t('growth.paidMedia.accountDetail.messages.notFound')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/growth/paid-media/accounts']">{{ t('catalog.action.backToList') }}</a>
          <div class="toolbar-actions">
            @if (account()!.isEnabled) {
              <button
                pButton
                type="button"
                severity="danger"
                [label]="t('growth.paidMedia.accounts.actions.disable')"
                [loading]="actionType() === 'disable'"
                (click)="disable()"></button>
            }
            @if (!account()!.killSwitchEnabled) {
              <button
                pButton
                type="button"
                severity="warn"
                [label]="t('growth.paidMedia.accounts.actions.enableKillSwitch')"
                (click)="openKillSwitchDialog()"></button>
            } @else {
              <button
                pButton
                type="button"
                [label]="t('growth.paidMedia.accounts.actions.clearKillSwitch')"
                [loading]="actionType() === 'killSwitch'"
                (click)="clearKillSwitch()"></button>
            }
            <a class="link-btn" [routerLink]="['/growth/paid-media/campaign-links']" [queryParams]="{ accountId: accountId }">
              {{ t('growth.paidMedia.accountDetail.openCampaignLinks') }}
            </a>
          </div>
        </div>

        <section class="card">
          <h3>{{ t('growth.paidMedia.accountDetail.sections.metadata') }}</h3>
          <ain-info-hint
            [titleKey]="'growth.paidMedia.accountDetail.help.metadata.title'"
            [descriptionKey]="'growth.paidMedia.accountDetail.help.metadata.description'" />
          <div class="grid">
            <label>
              <span>{{ t('growth.paidMedia.accounts.fields.providerKey') }}</span>
              <input pInputText type="text" [ngModel]="providerLabel(account()!.providerKey)" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.paidMedia.accounts.fields.connectorKey') }}</span>
              <input pInputText type="text" [ngModel]="account()!.connectorKey" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.paidMedia.accounts.fields.nationCode') }}</span>
              <input pInputText type="text" [ngModel]="account()!.nationCode" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.paidMedia.accounts.fields.authConfigRef') }}</span>
              <input pInputText type="text" [ngModel]="account()!.authConfigRef || '-'" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.paidMedia.accounts.columns.isEnabled') }}</span>
              <input pInputText type="text" [ngModel]="account()!.isEnabled ? t('common.yes') : t('common.no')" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.paidMedia.accounts.columns.canBeEnabled') }}</span>
              <input pInputText type="text" [ngModel]="account()!.canBeEnabled ? t('common.yes') : t('common.no')" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.paidMedia.accounts.columns.killSwitch') }}</span>
              <input pInputText type="text" [ngModel]="account()!.killSwitchEnabled ? t('common.yes') : t('common.no')" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.paidMedia.accounts.fields.killSwitchReason') }}</span>
              <input pInputText type="text" [ngModel]="account()!.killSwitchReason || '-'" [disabled]="true" />
            </label>
          </div>
        </section>

        <section class="card">
          <h3>{{ t('growth.paidMedia.accountDetail.sections.residency') }}</h3>
          <ain-info-hint
            [titleKey]="'growth.paidMedia.accountDetail.help.residency.title'"
            [descriptionKey]="'growth.paidMedia.accountDetail.help.residency.description'" />
          <p class="disclosure">{{ residencyMessage() }}</p>
        </section>

        <section class="card">
          <h3>{{ t('growth.paidMedia.accountDetail.sections.capabilities') }}</h3>
          <ain-info-hint
            [titleKey]="'growth.paidMedia.accountDetail.help.capabilities.title'"
            [descriptionKey]="'growth.paidMedia.accountDetail.help.capabilities.description'" />
          <ain-structured-json-display [fields]="capabilitiesFields" [payloadJson]="account()!.capabilitiesJson" />
        </section>

        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
        @if (successMessage()) {
          <p class="message success">{{ successMessage() }}</p>
        }
      }
    </ain-page>

    <p-dialog
      [header]="t('growth.paidMedia.accounts.killSwitchDialog.title')"
      [visible]="killSwitchDialogVisible()"
      (visibleChange)="killSwitchDialogVisible.set($event)"
      [modal]="true">
      <ain-form-field [label]="t('growth.paidMedia.accounts.fields.killSwitchReason')">
        <input pInputText type="text" [(ngModel)]="killSwitchReason" />
      </ain-form-field>
      <ng-template pTemplate="footer">
        <button pButton type="button" [label]="t('common.cancel')" severity="secondary" (click)="killSwitchDialogVisible.set(false)"></button>
        <button
          pButton
          type="button"
          severity="warn"
          [label]="t('growth.paidMedia.accounts.actions.enableKillSwitch')"
          [loading]="actionType() === 'killSwitch'"
          (click)="confirmKillSwitch()"></button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap; }
    .toolbar-actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .back-link { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
    .link-btn { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .link-btn:hover { text-decoration: underline; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 10px; }
    .card h3 { margin: 0; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    label { display: grid; gap: 6px; }
    .disclosure { margin: 0; color: #334155; line-height: 1.5; }
    .message.error { color: #b91c1c; }
    .message.success { color: #15803d; }
    @media (max-width: 960px) { .grid { grid-template-columns: 1fr 1fr; } }
    @media (max-width: 640px) { .grid { grid-template-columns: 1fr; } }
  `]
})
export class GrowthPaidMediaAccountDetailComponent implements OnInit {
  private readonly api = inject(GrowthPaidMediaService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly account = signal<GrowthPaidMediaAccountDto | null>(null);
  readonly errorMessage = signal('');
  readonly successMessage = signal('');
  readonly actionType = signal<'disable' | 'killSwitch' | null>(null);
  readonly killSwitchDialogVisible = signal(false);
  readonly capabilitiesFields = growthPaidMediaCapabilitiesFields;

  accountId = '';
  killSwitchReason = '';
  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.accountId = this.route.snapshot.paramMap.get('accountId') ?? '';

    this.liveUpdateService.events$
      .pipe(
        filter(event => {
          if (event.eventType !== LiveUpdateEventTypes.growthPaidMediaChanged) {
            return false;
          }
          const payloadId = event.payload['accountId'];
          return payloadId === undefined || payloadId === null || String(payloadId).toLowerCase() === this.accountId.toLowerCase();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.load(false));

    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    void this.setupLiveSubscription();
    this.load(true);
  }

  providerLabel(providerKey: string): string {
    return this.t(`growth.paidMedia.providers.${providerKey}`);
  }

  residencyMessage(): string {
    const key = this.api.residencyDisclosureKey(this.account());
    if (key) {
      return this.t(key);
    }
    return this.t('growth.paidMedia.residency.ok');
  }

  disable(): void {
    this.actionType.set('disable');
    this.errorMessage.set('');
    this.successMessage.set('');
    this.api.disableAccount(this.accountId).pipe(finalize(() => this.actionType.set(null))).subscribe({
      next: () => {
        this.successMessage.set(this.t('growth.paidMedia.accounts.messages.disableSucceeded'));
        this.load(false);
      },
      error: () => this.errorMessage.set(this.t('growth.paidMedia.accounts.messages.disableFailed'))
    });
  }

  openKillSwitchDialog(): void {
    this.killSwitchReason = '';
    this.killSwitchDialogVisible.set(true);
  }

  confirmKillSwitch(): void {
    this.actionType.set('killSwitch');
    this.errorMessage.set('');
    this.successMessage.set('');
    this.api.setKillSwitch(this.accountId, {
      enabled: true,
      reason: this.killSwitchReason.trim() || null
    }).pipe(finalize(() => this.actionType.set(null))).subscribe({
      next: () => {
        this.killSwitchDialogVisible.set(false);
        this.successMessage.set(this.t('growth.paidMedia.accounts.messages.killSwitchSucceeded'));
        this.load(false);
      },
      error: () => this.errorMessage.set(this.t('growth.paidMedia.accounts.messages.killSwitchFailed'))
    });
  }

  clearKillSwitch(): void {
    this.actionType.set('killSwitch');
    this.errorMessage.set('');
    this.successMessage.set('');
    this.api.setKillSwitch(this.accountId, { enabled: false }).pipe(finalize(() => this.actionType.set(null))).subscribe({
      next: () => {
        this.successMessage.set(this.t('growth.paidMedia.accounts.messages.killSwitchCleared'));
        this.load(false);
      },
      error: () => this.errorMessage.set(this.t('growth.paidMedia.accounts.messages.killSwitchFailed'))
    });
  }

  load(showLoading = true): void {
    if (!this.accountId) {
      this.loading.set(false);
      this.account.set(null);
      return;
    }
    if (showLoading) {
      this.loading.set(true);
    }
    this.errorMessage.set('');
    this.api.getAccount(this.accountId).subscribe({
      next: row => {
        this.account.set(row);
        this.loading.set(false);
      },
      error: () => {
        this.account.set(null);
        this.loading.set(false);
        this.errorMessage.set(this.t('growth.paidMedia.accountDetail.messages.loadFailed'));
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthPaidMediaTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
